/**
 * Billing Service
 * Handles billing and subscription API calls
 * Supports two billing models:
 * 1. Post-paid (Enterprise): Per-trip fee + revenue share (OurBus)
 * 2. Token-based (Standard): Prepaid tokens for API usage
 */

import api from './api';
import { API_ENDPOINTS } from '../config/environment';

// Billing plan types
export const BILLING_TYPES = {
  TOKEN_BASED: 'token_based',
  POST_PAID: 'post_paid',
};

// OurBus Enterprise Pricing (defaults - actual values from API)
export const OURBUS_PRICING = {
  PER_TRIP_FEE: 6.5, // $6.50 per trip
  REVENUE_SHARE_PERCENT: 0.25, // 0.25% of gross revenue
};

// Token-based pricing tiers
export const TOKEN_PLANS = {
  starter: {
    name: 'Starter',
    tokens: 10000,
    price: 55000, // INR
    pricePerToken: 5.50,
  },
  pro: {
    name: 'Pro',
    tokens: 25000,
    price: 125000,
    pricePerToken: 5.00,
  },
  business: {
    name: 'Business',
    tokens: 50000,
    price: 240000,
    pricePerToken: 4.80,
  },
  enterprise: {
    name: 'Enterprise',
    tokens: 'unlimited',
    price: 'custom',
    pricePerToken: 'custom',
    billing_type: BILLING_TYPES.POST_PAID,
  },
};

const billingService = {
  /**
   * Get current subscription with billing type detection
   * @returns {Promise<Object>} Subscription details
   */
  getSubscription: async () => {
    const response = await api.get(API_ENDPOINTS.BILLING_SUBSCRIPTION);
    const data = response.data;
    return {
      id: `sub_${data.client_id}`,
      tenant_id: data.client_id,
      plan: data.billing_type === BILLING_TYPES.POST_PAID ? 'enterprise' : 'pro',
      billing_type: data.billing_type,
      per_trip_fee: data.per_trip_fee,
      revenue_share_percent: data.revenue_share_percent,
      token_balance: data.token_balance,
      tokens_included: data.tokens_included_monthly,
      price_per_token: data.price_per_token,
      currency: data.currency,
      status: data.status,
      billing_cycle: data.billing_cycle,
      payment_terms: data.payment_terms,
      auto_renew: data.auto_renew,
      contract_start: data.contract_start,
      contract_end: data.contract_end,
    };
  },

  /**
   * Get real-time current charges for the current billing period
   * @returns {Promise<Object>} Current charges breakdown
   */
  getCurrentCharges: async () => {
    const response = await api.get(API_ENDPOINTS.BILLING_CURRENT_CHARGES);
    return response.data;
  },

  /**
   * Get billing summary for current period
   * @returns {Promise<Object>} Billing summary
   */
  getBillingSummary: async (month) => {
    const params = month ? `?month=${month}` : '';
    const response = await api.get(`${API_ENDPOINTS.BILLING_SUMMARY}${params}`);
    return response.data;
  },

  /**
   * Get token balance for current client
   * @returns {Promise<Object>} Token balance info
   */
  getTokenBalance: async () => {
    const response = await api.get(API_ENDPOINTS.BILLING_TOKEN_BALANCE);
    return response.data;
  },

  /**
   * Get usage history
   */
  getUsage: async ({ period = 'month' } = {}) => {
    const response = await api.get(`${API_ENDPOINTS.BILLING_USAGE}?period=${period}`);
    return response.data;
  },

  /**
   * Get invoices
   */
  getInvoices: async ({ page = 1, limit = 10 } = {}) => {
    const response = await api.get(`${API_ENDPOINTS.BILLING_INVOICES}?page=${page}&limit=${limit}`);
    return response.data;
  },

  /**
   * Create top-up order (legacy - uses billing endpoint)
   */
  createTopUp: async ({ tokens, gateway = 'razorpay' }) => {
    const response = await api.post(API_ENDPOINTS.BILLING_TOPUP, { tokens, gateway });
    return response.data;
  },

  /**
   * Create payment order via payment gateway
   * Supports Razorpay and Eazybuzz
   */
  createPaymentOrder: async ({ gateway = 'razorpay', tokens, email, name, phone }) => {
    const response = await api.post(API_ENDPOINTS.PAYMENT_CREATE_ORDER, {
      gateway,
      tokens,
      email,
      name,
      phone,
    });
    return response.data;
  },

  /**
   * Verify payment after completion
   */
  verifyPayment: async ({ gateway, order_id, payment_data }) => {
    const response = await api.post(API_ENDPOINTS.PAYMENT_VERIFY, {
      gateway,
      order_id,
      payment_data,
    });
    return response.data;
  },

  /**
   * Get available payment gateways
   */
  getPaymentGateways: async () => {
    const response = await api.get(API_ENDPOINTS.PAYMENT_GATEWAYS);
    return response.data;
  },

  /**
   * Calculate token price with discounts
   */
  calculatePrice: (tokens) => {
    const basePricePerToken = 5.50;

    let discount = 0;
    if (tokens >= 100000) discount = 0.20;
    else if (tokens >= 50000) discount = 0.15;
    else if (tokens >= 25000) discount = 0.10;
    else if (tokens >= 10000) discount = 0.05;

    const subtotal = tokens * basePricePerToken;
    const discountAmount = subtotal * discount;
    const total = subtotal - discountAmount;

    return {
      tokens,
      pricePerToken: basePricePerToken,
      subtotal,
      discount: discount * 100,
      discountAmount,
      total,
      currency: 'INR',
    };
  },

  /**
   * Calculate enterprise bill (post-paid)
   */
  calculateEnterpriseBill: (trips, grossRevenue) => {
    const perTripCharge = trips * OURBUS_PRICING.PER_TRIP_FEE;
    const revenueShareCharge = grossRevenue * (OURBUS_PRICING.REVENUE_SHARE_PERCENT / 100);

    return {
      trips,
      grossRevenue,
      perTripFee: OURBUS_PRICING.PER_TRIP_FEE,
      perTripCharge,
      revenueSharePercent: OURBUS_PRICING.REVENUE_SHARE_PERCENT,
      revenueShareCharge,
      totalBill: perTripCharge + revenueShareCharge,
      currency: 'USD',
    };
  },

  /**
   * Get billing type for tenant
   */
  getBillingType: (tenantId) => {
    if (tenantId === 'ourbus' || tenantId === 'ourbus-production') {
      return BILLING_TYPES.POST_PAID;
    }
    return BILLING_TYPES.TOKEN_BASED;
  },

  // =========================================================================
  // Admin API Methods (require admin API key)
  // =========================================================================

  /**
   * Get all billing plans (admin only)
   */
  adminGetAllPlans: async () => {
    const response = await api.get(API_ENDPOINTS.ADMIN_BILLING_PLANS);
    return response.data;
  },

  /**
   * Get billing plan for a specific client (admin only)
   */
  adminGetPlan: async (clientId) => {
    const response = await api.get(API_ENDPOINTS.ADMIN_BILLING_PLAN(clientId));
    return response.data;
  },

  /**
   * Create or update billing plan (admin only)
   */
  adminSavePlan: async (clientId, planData) => {
    const response = await api.post(API_ENDPOINTS.ADMIN_BILLING_PLAN(clientId), planData);
    return response.data;
  },

  /**
   * Update billing plan (admin only)
   */
  adminUpdatePlan: async (clientId, updates) => {
    const response = await api.put(API_ENDPOINTS.ADMIN_BILLING_PLAN(clientId), updates);
    return response.data;
  },
};

export default billingService;
